import React, { useEffect, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { BsFillSendFill } from "react-icons/bs";
import { FiImage } from "react-icons/fi";
import { LuCheckCheck } from "react-icons/lu";
import moment from "moment";
import { ticketApiService } from "../../../service/admin/ticketApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import "./style.css";

/**
 * Right-side slide-in chat panel for a ticket. Used on seller orders list,
 * seller order details, and admin order management instead of navigating to full ticket page.
 */
const DEFAULT_RIGHT = 16;
const DEFAULT_BOTTOM = 16;

export default function TicketChatPanel({ ticketId, orderLabel, onClose }) {
  const [ticketData, setTicketData] = useState(null);
  const [messageSent, setMessageSent] = useState("");
  const [files, setFiles] = useState([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState([]);
  const [panelPosition, setPanelPosition] = useState({ right: DEFAULT_RIGHT, bottom: DEFAULT_BOTTOM });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, right: 0, bottom: 0 });
  const fileUrlsRef = useRef([]);
  const chatBoxRef = useRef(null);
  const panelRef = useRef(null);
  const loader = useLoader();
  const message = useMessage();

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      right: panelPosition.right,
      bottom: panelPosition.bottom,
    };
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const { x, y, right, bottom } = dragStartRef.current;
      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;
      const panel = panelRef.current;
      const w = panel ? panel.offsetWidth : 360;
      const h = panel ? panel.offsetHeight : 420;
      const maxRight = window.innerWidth - w;
      const maxBottom = window.innerHeight - h;
      setPanelPosition({
        right: Math.max(0, Math.min(maxRight, right - deltaX)),
        bottom: Math.max(0, Math.min(maxBottom, bottom - deltaY)),
      });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  const fetchChat = () => {
    if (!ticketId) return;
    loader.showLoading(true);
    ticketApiService
      .getTicket(ticketId)
      .then((res) => {
        if (res.status.code === 200) {
          setTicketData(res.data);
        } else {
          message.showToast(res.error?.message || "Failed to load ticket", ToastTypes.ERROR);
        }
        loader.showLoading(false);
      })
      .catch((err) => {
        message.showToast(err?.message || "Failed to load ticket", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  useEffect(() => {
    if (ticketId) {
      setTicketData(null);
      setMessageSent("");
      setFiles([]);
      fetchChat();
    }
  }, [ticketId]);

  // Create and revoke object URLs for image previews
  useEffect(() => {
    fileUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    });
    const urls = files.map((f) => URL.createObjectURL(f));
    fileUrlsRef.current = urls;
    setFilePreviewUrls(urls);
    return () => {
      urls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
    };
  }, [files]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [ticketData, files]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!ticketId) return;
    if (!messageSent.trim() && files.length === 0) return;
    const payload = new FormData();
    payload.append("message", messageSent);
    files.forEach((f) => payload.append("images", f));
    loader.showLoading(true);
    ticketApiService
      .updateTicket(ticketId, payload)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setMessageSent("");
          setFiles([]);
          fetchChat();
        } else {
          message.showToast(res.error?.message || "Failed to send", ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Failed to send", ToastTypes.ERROR);
      });
  };

  const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  const handleAttachment = (e) => {
    const selected = e.target.files;
    if (!selected?.length) return;
    const newFiles = [...files];
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      if (!IMAGE_TYPES.includes(file.type)) {
        message.showToast("Only images (JPEG, PNG, GIF, WebP) can be uploaded", ToastTypes.WARNING);
        continue;
      }
      if (!newFiles.some((f) => f.name === file.name && f.size === file.size)) {
        newFiles.push(file);
      }
    }
    setFiles(newFiles);
    e.target.value = "";
  };

  const handleRemoveImg = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const displayOrderId = orderLabel || "—";

  const msgReadUnRead = (msg) => {
    if (!ticketData?.currentUser) return null;
    return msg.userId._id === ticketData.currentUser ? (
      <LuCheckCheck
        color={
          (msg.userId.role === "seller" && msg.seenByAdmin) ||
          (msg.userId.role === "admin" && msg.seenByUser)
            ? "#1bb9c4"
            : ""
        }
      />
    ) : null;
  };

  if (!ticketId) return null;

  return (
    <div className="ticket-chat-panel-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className={`ticket-chat-panel ${isDragging ? "ticket-chat-panel-dragging" : ""}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          right: panelPosition.right,
          bottom: panelPosition.bottom,
        }}
      >
        <div
          className="ticket-chat-panel-header ticket-chat-panel-drag-handle"
          onMouseDown={handleDragStart}
          title="Drag to move"
        >
          <div className="ticket-chat-panel-title">
            <span className="ticket-chat-panel-title-text">Ticket Support</span>
            <span className="ticket-chat-panel-order">Order: {displayOrderId}</span>
          </div>
          <button type="button" className="ticket-chat-panel-close" onClick={onClose} onMouseDown={(e) => e.stopPropagation()} aria-label="Close">
            <IoIosClose size={24} />
          </button>
        </div>

        {ticketData && (
          <>
            <div className="ticket-chat-panel-issue">{ticketData.issue}</div>
            <div className="ticket-chat-panel-messages" ref={chatBoxRef}>
              {ticketData?.chat?.map((msg, index) => (
                <React.Fragment key={index}>
                  {msg.message ? (
                    <div
                      className={
                        msg.userId._id === ticketData.currentUser
                          ? "ticket-chat-panel-msg ticket-chat-panel-msg-sent"
                          : "ticket-chat-panel-msg ticket-chat-panel-msg-received"
                      }
                    >
                      <p>
                        {msg.message}
                        <span className="ticket-chat-panel-msg-time">
                          {moment(msg.timestamp).format("h:mm a")} {msgReadUnRead(msg)}
                        </span>
                      </p>
                    </div>
                  ) : null}
                  {msg.attachments?.length >= 1 ? (
                    <div
                      className={
                        msg.userId._id === ticketData.currentUser
                          ? "ticket-chat-panel-img ticket-chat-panel-img-sent"
                          : "ticket-chat-panel-img ticket-chat-panel-img-received"
                      }
                    >
                      {msg.attachments.map((url, i) => (
                        <div key={i} className="ticket-chat-panel-img-wrap">
                          <a href={url} target="_blank" rel="noopener noreferrer" className="ticket-chat-panel-img-link">
                            <img src={url} alt="Attachment" loading="lazy" />
                          </a>
                          <span className="ticket-chat-panel-img-time">
                            {moment(msg.timestamp).format("h:mm a")} {msgReadUnRead(msg)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </React.Fragment>
              ))}
              <div className="ticket-chat-panel-files-preview">
                {files.map((f, i) => (
                  <div key={`${f.name}-${f.size}-${i}`} className="ticket-chat-panel-file-thumb">
                    <img src={filePreviewUrls[i]} alt="" />
                    <button type="button" className="ticket-chat-panel-file-remove" onClick={() => handleRemoveImg(i)} aria-label="Remove">
                      <IoIosClose size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <form className="ticket-chat-panel-form" onSubmit={handleSend}>
              <label htmlFor={`ticket-panel-file-${ticketId}`} className="ticket-chat-panel-attach" title="Add image">
                <FiImage size={20} />
              </label>
              <input
                id={`ticket-panel-file-${ticketId}`}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                onChange={handleAttachment}
                className="ticket-chat-panel-file-input"
              />
              <input
                type="text"
                value={messageSent}
                onChange={(e) => setMessageSent(e.target.value)}
                placeholder="Type a message..."
                className="ticket-chat-panel-input"
              />
              <button type="submit" className="ticket-chat-panel-send" aria-label="Send">
                <BsFillSendFill size={18} />
              </button>
            </form>
          </>
        )}
        {!ticketData && (
          <div className="ticket-chat-panel-loading">Loading ticket…</div>
        )}
      </div>
    </div>
  );
}
